import React, { useState, useEffect, useCallback } from "react";

import {
  listPiServers,
  createPiServer,
  updatePiServer,
  deletePiServer,
  getPiServerState,
  testPiServer,
} from "../../../../api";
import { useNotify } from "../../../../ToastContext";
import { usePermission } from "../../../../UserContext";
import Select from "../../../../components/Select";
import PasswordInput from "../../../../components/PasswordInput";
import plusIcon from "../../../../assets/plus.svg";
import xmarkIcon from "../../../../assets/xmark.svg";
import moreIcon from "../../../../assets/more-horiz.svg";

// 2 s state refresh — matches the snappiness of the underlying 1 s tag poll;
// faster would just spam /state with no visible win since the dot can only
// change when the poller's next tick fires.
const POLL_INTERVAL = 2000;

const AUTH_MODES = [
  { value: "basic", label: "Basic (Username / Password)" },
  { value: "anonymous", label: "Anonymous" },
];

const STATUS_COLORS = {
  connected: { bg: "rgba(76,175,80,0.12)", color: "rgba(76,175,80,0.95)", border: "rgba(76,175,80,0.25)", dot: "#4caf50" },
  idle:      { bg: "rgba(158,158,158,0.12)", color: "rgba(200,200,200,0.95)", border: "rgba(158,158,158,0.25)", dot: "#9e9e9e" },
  disabled:  { bg: "rgba(158,158,158,0.12)", color: "rgba(200,200,200,0.95)", border: "rgba(158,158,158,0.25)", dot: "#9e9e9e" },
  error:     { bg: "rgba(244,67,54,0.12)", color: "rgba(244,67,54,0.95)", border: "rgba(244,67,54,0.25)", dot: "#f44336" },
};

const StatusDot = ({ status }) => {
  const c = STATUS_COLORS[status] || STATUS_COLORS.idle;
  return (
    <span title={status} style={{ width: 8, height: 8, borderRadius: "50%", background: c.dot, flexShrink: 0 }} />
  );
};

const errorMessage = (err, fb) => {
  const m = String(err?.message || err).trim();
  return m || fb;
};

const INITIAL_SERVER = {
  name: "",
  baseUrl: "https://",
  authMode: "basic",
  username: "",
  password: "",
  clearPassword: false,
  tlsSkipVerify: false,
};

const ServerModal = ({ editing, onClose, onSaved }) => {
  const [form, setForm] = useState(() => {
    if (!editing) return INITIAL_SERVER;
    return {
      name: editing.name || "",
      baseUrl: editing.base_url || "https://",
      authMode: editing.auth_mode || "basic",
      username: editing.username || "",
      password: "",
      clearPassword: false,
      tlsSkipVerify: !!editing.tls_skip_verify,
    };
  });
  const [submitting, setSubmitting] = useState(false);
  const notify = useNotify();
  const isEdit = !!editing;

  const set = useCallback((field) => (e) => {
    const value = typeof e === "string" ? e : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const isBasicAuth = form.authMode === "basic";
    const payload = {
      name: form.name.trim(),
      base_url: form.baseUrl.trim(),
      auth_mode: form.authMode,
      username: isBasicAuth ? form.username.trim() : "",
      tls_skip_verify: !!form.tlsSkipVerify,
    };

    // Same password handling pattern as OPC UA: blank on edit means "keep
    // existing" unless the operator switched off Basic auth or explicitly
    // checked Remove. On create, only attach a non-empty password when the
    // user is actually using basic auth.
    if (isEdit) {
      if (!isBasicAuth) {
        payload.password = "";
      } else if (form.password) {
        payload.password = form.password;
      } else if (form.clearPassword) {
        payload.password = "";
      }
    } else if (isBasicAuth && form.password) {
      payload.password = form.password;
    }

    try {
      if (isEdit) {
        await updatePiServer(editing.id, payload);
      } else {
        await createPiServer(payload);
      }
      notify({ type: "success", title: isEdit ? "Updated" : "Created", message: `Server "${payload.name || payload.base_url}" ${isEdit ? "updated" : "created"}.` });
      onSaved();
      onClose();
    } catch (err) {
      notify({ type: "error", title: isEdit ? "Update Failed" : "Create Failed", message: errorMessage(err, "Unknown error") });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="gwOverlay" onClick={onClose}>
      <form className="gwModal" onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit} style={{ width: 560 }}>
        <div className="gwModalHeader">
          <div>
            <div className="gwModalTitle">{isEdit ? `Edit: ${editing.name || editing.id}` : "New PI Server"}</div>
            <div className="gwModalSubtitle">{isEdit ? "Update the server configuration." : "Configure a connection to AVEVA / OSIsoft PI Web API."}</div>
          </div>
          <button className="gwModalClose" type="button" onClick={onClose}>
            <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
          </button>
        </div>
        <div className="gwModalBody">
          <div className="gwField">
            <label className="gwLabel">Name</label>
            <input className="gwInput" placeholder="e.g. plant-pi" value={form.name} onChange={set("name")} autoFocus={!isEdit} />
            <div className="gwHint">Display name. Must be unique (case-insensitive) if set.</div>
          </div>
          <div className="gwField">
            <label className="gwLabel">PI Web API Base URL</label>
            <input
              className="gwInput"
              placeholder="https://pi-server.example.com/piwebapi"
              value={form.baseUrl}
              onChange={set("baseUrl")}
              required
              autoFocus={isEdit}
            />
            <div className="gwHint">URL of the PI Web API service (typically ends in <code>/piwebapi</code>). Must start with <code>http://</code> or <code>https://</code>.</div>
          </div>
          <div className="gwField">
            <label className="gwLabel">Authentication</label>
            <Select value={form.authMode} onChange={set("authMode")} options={AUTH_MODES} />
          </div>
          {form.authMode === "basic" && (
            <>
              <div className="gwFieldRow">
                <div className="gwField">
                  <label className="gwLabel">Username</label>
                  <input className="gwInput" value={form.username} onChange={set("username")} required />
                </div>
                <div className="gwField">
                  <label className="gwLabel">Password</label>
                  <PasswordInput
                    placeholder={isEdit && editing.has_password && !form.clearPassword ? "Leave blank to keep current" : ""}
                    value={form.password}
                    onChange={set("password")}
                    disabled={form.clearPassword}
                  />
                </div>
              </div>
              {isEdit && editing.has_password && (
                <div className="gwField">
                  <label className="gwLabel" style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: 0 }}>
                    <input
                      type="checkbox"
                      checked={form.clearPassword}
                      onChange={(e) => setForm((p) => ({
                        ...p,
                        clearPassword: e.target.checked,
                        password: e.target.checked ? "" : p.password,
                      }))}
                    />
                    <span>Remove existing password</span>
                  </label>
                  <div className="gwHint">Clears the stored password. Use when the PI server now accepts the username without one.</div>
                </div>
              )}
            </>
          )}
          <div className="gwField">
            <label className="gwLabel" style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: 0 }}>
              <input
                type="checkbox"
                checked={form.tlsSkipVerify}
                onChange={(e) => setForm((p) => ({ ...p, tlsSkipVerify: e.target.checked }))}
              />
              <span>Skip TLS certificate verification</span>
            </label>
            <div className="gwHint">Use only for self-signed certs in dev environments. Production PI servers should present a CA-signed cert.</div>
          </div>
        </div>
        <div className="gwModalFooter">
          <button className="gwGhostBtn" type="button" onClick={onClose}>Cancel</button>
          <button className="gwPrimaryBtn" type="submit" disabled={submitting}>
            {submitting ? "Saving..." : isEdit ? "Save" : "Create Server"}
          </button>
        </div>
      </form>
    </div>
  );
};

export const PiConnectionsPage = () => {
  const canConfigure = usePermission("configure");
  const [servers, setServers] = useState([]);
  const [states, setStates] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);
  const [menuStyle, setMenuStyle] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [testing, setTesting] = useState(null);
  const notify = useNotify();

  const fetchAll = useCallback(async () => {
    try {
      const list = (await listPiServers()) || [];
      setServers(list);
      const stateResults = await Promise.allSettled(
        list.map((s) => getPiServerState(s.id).then((st) => [s.id, st]))
      );
      setStates((prev) => {
        const next = { ...prev };
        for (const r of stateResults) {
          if (r.status === "fulfilled") {
            const [id, st] = r.value;
            next[id] = st;
          }
        }
        const liveIds = new Set(list.map((s) => s.id));
        for (const id of Object.keys(next)) {
          if (!liveIds.has(id)) delete next[id];
        }
        return next;
      });
    } catch (err) {
      notify({ type: "error", title: "Error", message: errorMessage(err, "Failed to load servers") });
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [fetchAll]);

  const openMenu = (e, id) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    setMenuStyle({ position: "fixed", top: `${Math.round(r.bottom + 4)}px`, right: `${Math.max(12, Math.round(window.innerWidth - r.right))}px` });
    setMenuOpen(menuOpen === id ? null : id);
  };

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => {
      const menu = document.querySelector(".gwProjectMenu");
      if (menu?.contains(e.target)) return;
      setMenuOpen(null);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deletePiServer(deleteTarget.id);
      notify({ type: "success", title: "Removed", message: `Server "${deleteTarget.name || deleteTarget.id}" deleted.` });
      setDeleteTarget(null);
      fetchAll();
    } catch (err) {
      notify({ type: "error", title: "Delete Failed", message: errorMessage(err, "Unknown error") });
    } finally {
      setDeleting(false);
    }
  };

  const runTest = async (s) => {
    setTesting(s.id);
    try {
      await testPiServer(s.id);
      notify({ type: "success", title: "Connection OK", message: `${s.name || s.id} responded.` });
    } catch (err) {
      notify({ type: "error", title: "Test Failed", message: errorMessage(err, "PI Web API did not respond") });
    } finally {
      setTesting(null);
    }
  };

  const hasServers = servers.length > 0;

  return (
    <div className="gwContentGrid">
      <section className="gwFormStack">
        <div className="gwPageHeader">
          <div>
            <div className="gwPageTitle">PI System</div>
            <div className="gwPageSubtitle">
              {hasServers ? `${servers.length} server${servers.length !== 1 ? "s" : ""} configured` : "Configure PI Web API servers to federate history reads from AVEVA / OSIsoft PI."}
            </div>
          </div>
          <button
            className="gwPrimaryBtn"
            type="button"
            onClick={() => setShowCreate(true)}
            disabled={!canConfigure}
            title={canConfigure ? undefined : "Requires configure permission"}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <img src={plusIcon} alt="" className="gwIcon" style={{ width: 14, height: 14 }} />
            <span>Add Server</span>
          </button>
        </div>

        {loading && !hasServers && (
          <div style={{ padding: 24, textAlign: "center", color: "var(--gw-muted)", fontSize: 13 }}>Loading servers...</div>
        )}

        {!loading && !hasServers && (
          <div className="gwEmptyState">
            <div className="gwEmptyTitle">No servers yet</div>
            <div className="gwEmptyBody">Add a PI Web API server to start binding tags to PI points.</div>
            <div className="gwEmptyActions">
              <button
                className="gwPrimaryBtn"
                type="button"
                onClick={() => setShowCreate(true)}
                disabled={!canConfigure}
                title={canConfigure ? undefined : "Requires configure permission"}
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <img src={plusIcon} alt="" className="gwIcon" style={{ width: 14, height: 14 }} />
                <span>Add Server</span>
              </button>
            </div>
          </div>
        )}

        {hasServers && (
          <div className="gwCardSection">
            <div className="gwSectionTitle">Servers</div>
            <div className="gwCardSectionBody" style={{ padding: 0 }}>
              {servers.map((s) => {
                const st = states[s.id];
                const status = st?.status || "idle";
                const colors = STATUS_COLORS[status] || STATUS_COLORS.idle;
                return (
                  <div key={s.id}>
                    <div className="umUserRow">
                      <div
                        style={{
                          width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                          background: colors.bg,
                          border: `1px solid ${colors.border}`,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 11, fontWeight: 700, color: colors.color,
                        }}
                      >
                        PI
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span className="umUserName">{s.name || s.id}</span>
                          <StatusDot status={status} />
                        </div>
                        <div className="umUserMeta">
                          {s.base_url}
                          {s.auth_mode === "basic" ? " · basic" : " · anonymous"}
                          {s.tls_skip_verify ? " · TLS verify off" : ""}
                          {st?.last_error_msg && (
                            <span style={{ color: "var(--gw-danger)" }} title={st.last_error_msg}> &middot; {st.last_error_msg}</span>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="gwIconButton"
                        style={{ flexShrink: 0 }}
                        onClick={(e) => openMenu(e, s.id)}
                      >
                        <img src={moreIcon} alt="More" className="gwIcon" style={{ width: 16, height: 16 }} />
                      </button>
                      {menuOpen === s.id && (
                        <div className="gwProjectMenu" style={menuStyle} onClick={(e) => e.stopPropagation()}>
                          <button
                            className="gwProjectMenuItem"
                            type="button"
                            disabled={testing === s.id}
                            onClick={() => { setMenuOpen(null); runTest(s); }}
                          >
                            {testing === s.id ? "Testing..." : "Test Connection"}
                          </button>
                          <button
                            className="gwProjectMenuItem"
                            type="button"
                            disabled={!canConfigure}
                            title={canConfigure ? undefined : "Requires configure permission"}
                            onClick={() => { setMenuOpen(null); setEditing(s); }}
                          >
                            Edit
                          </button>
                          <div className="gwProfileDivider" />
                          <button
                            className="gwProjectMenuItem gwProjectMenuDanger"
                            type="button"
                            disabled={!canConfigure}
                            title={canConfigure ? undefined : "Requires configure permission"}
                            onClick={() => { setMenuOpen(null); setDeleteTarget(s); }}
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {showCreate && (
        <ServerModal onClose={() => setShowCreate(false)} onSaved={fetchAll} />
      )}
      {editing && (
        <ServerModal editing={editing} onClose={() => setEditing(null)} onSaved={fetchAll} />
      )}

      {deleteTarget && (
        <div className="gwOverlay" onClick={() => setDeleteTarget(null)}>
          <div className="gwModal" onClick={(e) => e.stopPropagation()} style={{ width: 420 }}>
            <div className="gwModalHeader">
              <div>
                <div className="gwModalTitle">Remove Server</div>
                <div className="gwModalSubtitle">Bound tag sources will be cleared.</div>
              </div>
              <button className="gwModalClose" type="button" onClick={() => setDeleteTarget(null)}>
                <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
              </button>
            </div>
            <div className="gwModalBody">
              <p style={{ fontSize: 13, color: "var(--gw-text-secondary)", margin: 0 }}>
                Remove server <strong>{deleteTarget.name || deleteTarget.id}</strong>? Any tags bound to this server will lose their source.
              </p>
            </div>
            <div className="gwModalFooter" style={{ justifyContent: "space-between" }}>
              <button className="gwGhostBtn" type="button" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="gwDangerBtn" type="button" onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Removing..." : "Remove Server"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
